import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { addDoc, collection, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, setDoc } from "firebase/firestore";
import { FiCheckCircle, FiClock, FiEye } from "react-icons/fi";
import { toast } from "sonner";
import { db } from "../../../firebase";
import { useAuth } from "../../../contexts/AuthContext";
import MaintenancePageShell from "../MaintenancePageShell";
import {
    ContentGrid, EditorPanel, FieldGroup, Hint, Input, Label, PreviewHeader, PreviewPanel, Textarea,
} from "../shared";

const formatReleaseIdTimestamp = (date) => {
    const pad = (value) => String(value).padStart(2, "0");
    return [
        date.getFullYear(),
        pad(date.getMonth() + 1),
        pad(date.getDate()),
        pad(date.getHours()),
        pad(date.getMinutes()),
    ].join("-");
};

export default function ReleaseNotesMaintenancePage() {
    const { user, isAdmin } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isProjectManager, setIsProjectManager] = useState(false);
    const [originalReleaseId, setOriginalReleaseId] = useState(null);
    const [releaseId, setReleaseId] = useState(
        () => `teamcast-${formatReleaseIdTimestamp(new Date())}`
    );
    const [title, setTitle] = useState("What's new");
    const [description, setDescription] = useState("Here are the latest changes available in this version.");
    const [notesText, setNotesText] = useState("");
    const [history, setHistory] = useState([]);

    const loadHistory = async () => {
        try {
            const snap = await getDocs(
                query(collection(db, "releaseHistory"), orderBy("publishedAt", "desc"), limit(30))
            );
            setHistory(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (error) {
            console.error("Failed to load release history:", error);
        }
    };

    const notes = useMemo(
        () => notesText.split("\n").map((note) => note.trim()).filter(Boolean),
        [notesText]
    );
    const homeLink = isAdmin ? "/admin/home" : "/student/home";

    useEffect(() => {
        const loadPage = async () => {
            if (!user?.email) return;
            setLoading(true);
            try {
                const permissionSnap = await getDoc(doc(db, "Users", user.email));
                const hasAccess = permissionSnap.exists() && permissionSnap.data()?.isProjectManager === true;
                setIsProjectManager(hasAccess);
                if (!hasAccess) return;

                await loadHistory();

                const snap = await getDoc(doc(db, "siteConfig", "releaseNotes"));
                if (snap.exists()) {
                    const data = snap.data();
                    setReleaseId(data.releaseId || `teamcast-${formatReleaseIdTimestamp(new Date())}`);
                    setOriginalReleaseId(data.releaseId || null);
                    setTitle(data.title || "What's new");
                    setDescription(data.description || "Here are the latest changes available in this version.");
                    setNotesText(Array.isArray(data.notes) ? data.notes.join("\n") : "");
                }
            } catch (error) {
                console.error("Failed to load release notes:", error);
                toast.error("Failed to load release note settings.");
            } finally {
                setLoading(false);
            }
        };
        loadPage();
    }, [user]);

    const executeSave = async (publish) => {
        setSaving(true);
        try {
            const payload = {
                releaseId: releaseId.trim(),
                title: title.trim(),
                description: description.trim(),
                notes,
            };
            await setDoc(doc(db, "siteConfig", "releaseNotes"), {
                ...payload,
                enabled: publish,
                updatedAt: serverTimestamp(),
                updatedBy: user.email,
            }, { merge: true });
            if (publish) {
                await addDoc(collection(db, "releaseHistory"), {
                    ...payload,
                    publishedAt: serverTimestamp(),
                    publishedBy: user.email,
                });
                await loadHistory();
            }
            setOriginalReleaseId(releaseId.trim());
            toast.success(publish ? "Update notes published." : "Draft saved.");
        } catch (error) {
            console.error("Failed to save release notes:", error);
            toast.error("Failed to save update notes.");
        } finally {
            setSaving(false);
        }
    };

    const handleSave = (publish) => {
        if (!isProjectManager || saving) return;
        if (!releaseId.trim()) { toast.error("Release ID is required."); return; }
        if (!title.trim()) { toast.error("Title is required."); return; }
        if (notes.length === 0) { toast.error("Add at least one update item."); return; }

        if (publish && originalReleaseId !== null && releaseId.trim() === originalReleaseId) {
            toast.warning("Release ID unchanged", {
                description: "Some users won't see this.",
                action: { label: "Publish anyway", onClick: () => executeSave(true) },
                duration: 8000,
            });
            return;
        }
        executeSave(publish);
    };

    return (
        <MaintenancePageShell
            title="Maintenance Update Notes"
            subtitle="Users will see the popup once for each new release ID."
            loading={loading}
            isProjectManager={isProjectManager}
            saving={saving}
            onSaveDraft={() => handleSave(false)}
            onPublish={() => handleSave(true)}
            homeLink={homeLink}
        >
            <ContentGrid>
                <EditorPanel>
                    <FieldGroup>
                        <Label htmlFor="release-id">Release ID</Label>
                        <Input
                            id="release-id"
                            value={releaseId}
                            onChange={(e) => setReleaseId(e.target.value)}
                        />
                        <Hint>Change this ID every time you want users to see a new popup.</Hint>
                    </FieldGroup>

                    <FieldGroup>
                        <Label htmlFor="release-title">Title</Label>
                        <Input
                            id="release-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="What's new"
                        />
                    </FieldGroup>

                    <FieldGroup>
                        <Label htmlFor="release-description">Description</Label>
                        <Textarea
                            id="release-description"
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </FieldGroup>

                    <FieldGroup>
                        <Label htmlFor="release-notes">Update items</Label>
                        <Textarea
                            id="release-notes"
                            rows={9}
                            value={notesText}
                            onChange={(e) => setNotesText(e.target.value)}
                            placeholder={"Added Google Docs support in activities.\nImproved artifact visibility with course settings.\nFixed activity submission reliability issues."}
                        />
                        <Hint>One update per line.</Hint>
                    </FieldGroup>
                </EditorPanel>

                <PreviewPanel>
                    <PreviewHeader>
                        <FiEye size={18} />
                        <span>Preview</span>
                    </PreviewHeader>
                    <PreviewCard>
                        <PreviewEyebrow>TeamCAST update</PreviewEyebrow>
                        <PreviewTitle>{title || "What's new"}</PreviewTitle>
                        <PreviewDescription>
                            {description || "Here are the latest changes available in this version."}
                        </PreviewDescription>
                        <PreviewList>
                            {(notes.length ? notes : ["Your update item will appear here."]).map((note, i) => (
                                <PreviewItem key={i}>
                                    <FiCheckCircle size={18} />
                                    <span>{note}</span>
                                </PreviewItem>
                            ))}
                        </PreviewList>
                        <PreviewButton>Got it</PreviewButton>
                    </PreviewCard>
                </PreviewPanel>
            </ContentGrid>
            {isProjectManager && history.length > 0 && (
                <HistorySection>
                    <HistoryTitle>
                        <FiClock size={16} />
                        Publication history
                    </HistoryTitle>
                    <HistoryList>
                        {history.map((entry) => {
                            const date = entry.publishedAt?.seconds
                                ? new Date(entry.publishedAt.seconds * 1000).toLocaleString()
                                : "—";
                            return (
                                <HistoryItem key={entry.id}>
                                    <HistoryMeta>
                                        <HistoryRelId>{entry.releaseId}</HistoryRelId>
                                        <HistoryDateBy>{date} · {entry.publishedBy}</HistoryDateBy>
                                    </HistoryMeta>
                                    <HistoryItemTitle>{entry.title}</HistoryItemTitle>
                                    <HistoryNotes>
                                        {entry.notes?.map((note) => (
                                            <li key={note}>{note}</li>
                                        ))}
                                    </HistoryNotes>
                                </HistoryItem>
                            );
                        })}
                    </HistoryList>
                </HistorySection>
            )}
        </MaintenancePageShell>
    );
}

const PreviewCard = styled.div`
    padding: 24px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
`;

const PreviewEyebrow = styled.p`
    margin: 0 0 6px;
    color: #0f766e;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
`;

const PreviewTitle = styled.h2`
    margin: 0;
    color: #111827;
    font-size: 1.45rem;
    line-height: 1.2;
    letter-spacing: 0;
`;

const PreviewDescription = styled.p`
    margin: 10px 0 18px;
    color: #4b5563;
    font-size: 0.94rem;
    line-height: 1.5;
`;

const PreviewList = styled.ul`
    display: grid;
    gap: 11px;
    margin: 0 0 22px;
    padding: 0;
    list-style: none;
`;

const PreviewItem = styled.li`
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr);
    gap: 10px;
    align-items: start;
    color: #374151;
    font-size: 0.92rem;
    line-height: 1.45;

    svg {
        margin-top: 2px;
        color: #16a34a;
    }
`;

const PreviewButton = styled.button`
    width: 100%;
    min-height: 42px;
    border: none;
    border-radius: 9999px;
    background: #9333ea;
    color: #ffffff;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.1s;

    &:hover {
        background: #7c3aed;
    }

    &:active {
        transform: scale(0.95);
    }
`;

const HistorySection = styled.section`
    margin-top: 28px;
    padding: 22px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
`;

const HistoryTitle = styled.h3`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 18px;
    color: #374151;
    font-size: 0.95rem;
    font-weight: 700;
`;

const HistoryList = styled.div`
    display: grid;
    gap: 0;
`;

const HistoryItem = styled.div`
    padding: 16px 0;
    border-top: 1px solid #f3f4f6;

    &:first-child {
        border-top: none;
        padding-top: 0;
    }
`;

const HistoryMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
    flex-wrap: wrap;
`;

const HistoryRelId = styled.span`
    padding: 2px 8px;
    border-radius: 4px;
    background: #f3f4f6;
    color: #374151;
    font-size: 0.78rem;
    font-family: monospace;
`;

const HistoryDateBy = styled.span`
    color: #9ca3af;
    font-size: 0.8rem;
`;

const HistoryItemTitle = styled.p`
    margin: 0 0 6px;
    color: #111827;
    font-size: 0.92rem;
    font-weight: 600;
`;

const HistoryNotes = styled.ul`
    margin: 0;
    padding-left: 18px;
    display: grid;
    gap: 4px;
    color: #6b7280;
    font-size: 0.88rem;
    line-height: 1.45;
`;
